import { useEffect, useCallback, useState } from 'react';
import { useHistoryStore, HistoryAction } from '@/stores/historyStore';
import { useCanvasStore, type Item } from '@/stores/canvasStore';
import { useArrowStore, type Arrow } from '@/stores/arrowStore';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import { isFocusInTextOrOverlay } from '@/lib/canvasEventGuards';
import { enqueueMutation, flush } from '@/lib/mutationQueue';

// Set by the mounted useUndoRedo hook, so a toast's Undo runs the same path as Cmd+Z
let runUndo: (() => Promise<void>) | null = null;
// Set by the mounted hook too: undoes one Partnr message's group, wherever it sits in the stack
let runGroupUndo: ((key: string) => Promise<boolean>) | null = null;

const DELETE_TOAST_DURATION_MS = 6000;

/**
 * Record a delete in the undo history and show a toast with an Undo button.
 * The button only undoes this delete if it is still the newest history entry.
 */
export function recordDeleteWithUndo(deletedItems: Item[]) {
  if (deletedItems.length === 0) return;
  const action: HistoryAction = {
    type: deletedItems.length > 1 ? 'DELETE_ITEMS' : 'DELETE_ITEM',
    timestamp: Date.now(),
    items: deletedItems,
  };
  useHistoryStore.getState().pushAction(action);

  const count = deletedItems.length;
  toast.success(`Deleted ${count} item${count === 1 ? '' : 's'}`, {
    duration: DELETE_TOAST_DURATION_MS,
    action: {
      label: 'Undo',
      onClick: () => {
        const { undoStack } = useHistoryStore.getState();
        if (undoStack[undoStack.length - 1] !== action || !runUndo) {
          toast.error("Can't undo: something changed after this delete");
          return;
        }
        void runUndo();
      },
    },
  });
}

/**
 * Undo everything one Partnr assistant message did (the receipt's Undo).
 * Works even when other changes came after it, as long as its group is still
 * in the five-step history. False when there is nothing left to undo or the
 * canvas is not mounted.
 */
export async function undoPartnrMessage(messageId: string): Promise<boolean> {
  if (!runGroupUndo || !useHistoryStore.getState().findGroup(messageId)) return false;
  return runGroupUndo(messageId);
}

/** Whether a Partnr message still has an undo step (for the receipt's Undo button). */
export function canUndoPartnrMessage(messageId: string): boolean {
  return !!useHistoryStore.getState().findGroup(messageId);
}

/**
 * Where a Partnr message's undo group sits: `ready` on the undo stack,
 * `undone` on the redo stack, `gone` when it left the five-step history or
 * never existed in this tab (a reply loaded from a saved chat).
 */
export type PartnrUndoState = 'ready' | 'undone' | 'gone';

export function partnrUndoState(stacks: { undoStack: HistoryAction[]; redoStack: HistoryAction[] }, messageId: string): PartnrUndoState {
  const isGroup = (a: HistoryAction) => a.type === 'GROUP' && a.groupKey === messageId;
  if (stacks.undoStack.some(isGroup)) return 'ready';
  if (stacks.redoStack.some(isGroup)) return 'undone';
  return 'gone';
}

// Replies undone in this tab, so "Undone" outlives the redo stack and a remount
const undoneReplies = new Set<string>();

/**
 * The undo state of one Partnr message, re-rendering when the history
 * changes, so a receipt's Undo disables the moment it runs (or Cmd+Z takes
 * the group) instead of staying live until something else re-renders it.
 */
export function usePartnrUndoState(messageId: string): PartnrUndoState {
  const live = useHistoryStore((s) => partnrUndoState(s, messageId));
  // A new edit clears the redo stack; the reply still reads "Undone" rather
  // than looking applied again, also after its row remounts
  const [seenUndone, setSeenUndone] = useState(() => undoneReplies.has(messageId));
  useEffect(() => {
    if (live === 'undone') {
      undoneReplies.add(messageId);
      setSeenUndone(true);
    } else if (live === 'ready') {
      undoneReplies.delete(messageId);
      setSeenUndone(false);
    }
  }, [live, messageId]);
  return live === 'gone' && seenUndone ? 'undone' : live;
}

/** Test hook. */
export function forgetUndoneReplies(): void {
  undoneReplies.clear();
}

type Direction = 'undo' | 'redo';

// One step at a time: a second Cmd+Z waits for the first to finish, so steps
// never interleave and each press takes exactly one entry off the stack.
let queue: Promise<void> = Promise.resolve();

function enqueue<T>(work: () => Promise<T>): Promise<T> {
  const next = queue.then(work);
  queue = next.then(() => {}, () => {});
  return next;
}

function enqueueStep(direction: Direction): Promise<void> {
  return enqueue(() => runStep(direction));
}

/** Replace an item's content wholesale (no merge), locally and in the save queue. */
function setItemContent(itemId: string, content: Record<string, unknown>) {
  const apply = (item: Item) => (item.id === itemId ? { ...item, content: content as Json } : item);
  useCanvasStore.setState((s) => ({ items: s.items.map(apply), allItems: s.allItems.map(apply) }));
  enqueueMutation(itemId, { content }, 'content');
}

/** Saves what is pending, then reloads the open board (after changes made straight in the database). */
async function reloadBoard() {
  // Pending saves first, so the reload can't bring back stale rows
  await flush();
  const { currentBoardId, fetchItems } = useCanvasStore.getState();
  if (currentBoardId) await fetchItems(currentBoardId);
}

async function moveToBoard(itemIds: string[], boardId: string, positions?: Map<string, { x: number; y: number }>, reload = true) {
  await flush();
  const { error } = await supabase.from('items').update({ board_id: boardId }).in('id', itemIds);
  if (error) throw error;
  for (const [id, p] of positions ?? []) {
    enqueueMutation(id, { position_x: p.x, position_y: p.y }, 'position');
  }
  // allItems feeds folder counts and previews; the board reload doesn't touch it
  const moving = new Set(itemIds);
  useCanvasStore.setState((s) => ({
    allItems: s.allItems.map((i) => {
      if (!moving.has(i.id)) return i;
      const p = positions?.get(i.id);
      return { ...i, board_id: boardId, ...(p ? { position_x: p.x, position_y: p.y } : {}) };
    }),
  }));
  if (reload) await reloadBoard();
}

/** Takes one entry off the undo or redo stack and applies it. */
async function runStep(direction: Direction) {
  const history = useHistoryStore.getState();
  const action = direction === 'undo' ? history.undo() : history.redo();
  if (!action) return;
  const label = direction === 'undo' ? 'Undo' : 'Redo';
  try {
    await applyStep(action, direction);
  } catch (error) {
    console.error(`${label} failed:`, error);
    toast.error(`${label} failed`);
  }
}

/** Undoes one Partnr message's group (the receipt's Undo), queued like a key press. */
function runGroupStep(key: string): Promise<boolean> {
  return enqueue(async () => {
    const group = useHistoryStore.getState().takeGroup(key);
    if (!group) return false;
    try {
      await applyStep(group, 'undo');
      return true;
    } catch (error) {
      console.error('Undo failed:', error);
      toast.error('Undo failed');
      return false;
    }
  });
}

/**
 * Applies one history entry in one direction. Changes go to the local store
 * and are saved through the mutation queue (or a direct insert/delete). Only
 * steps that change boards in the database (folders, moves between boards)
 * reload the open board, after flushing pending saves: a reload would
 * otherwise throw away edits not saved yet, which made one undo look like
 * several. `quiet` skips the toast and the reload, for a group's steps; the
 * return value says whether the group still owes a reload.
 */
async function applyStep(action: HistoryAction, direction: Direction, quiet = false): Promise<boolean> {
  const isUndo = direction === 'undo';
  const label = isUndo ? 'Undo' : 'Redo';
  const store = useCanvasStore.getState();
  const say = (message: string) => {
    if (!quiet) toast.success(message);
  };
  let reload = false;

  switch (action.type) {
    case 'GROUP': {
      // Undo goes last step first: a later step may sit inside a folder an earlier one made
      const children = isUndo ? [...(action.children ?? [])].reverse() : action.children ?? [];
      for (const child of children) {
        if (await applyStep(child, direction, true)) reload = true;
      }
      say(`${label}: ${action.label ?? 'changes'}`);
      break;
    }

    case 'CREATE_FOLDER': {
      if (!action.boardId) break;
      if (isUndo) {
        // Items it moved in go back first; deleting the folder deletes what is left inside
        if (action.itemIds?.length && action.previousBoardId) {
          await moveToBoard(action.itemIds, action.previousBoardId, action.previousPositions, false);
        }
        await store.deleteBoard(action.boardId);
        // deleteBoard only removes the folder card when its board is open; remove it wherever it is
        await supabase.from('items').delete().eq('item_type', 'board_link').eq('content->>linked_board_id', action.boardId);
        say('Undo: Folder removed');
      } else {
        const folder = action.newContent as { name?: string; parentId?: string; x?: number; y?: number; iconColor?: string } | undefined;
        if (!folder?.parentId) break;
        // createBoard puts the folder card on the open board, so redo only where the folder was made
        if (store.currentBoardId !== folder.parentId) {
          if (!quiet) toast.error('Open the board the folder was on to redo it');
          break;
        }
        await store.createBoard(folder.name ?? 'New Folder', folder.parentId, { x: folder.x ?? 0, y: folder.y ?? 0 }, {
          id: action.boardId,
          iconColor: folder.iconColor,
        });
        if (action.itemIds?.length) await moveToBoard(action.itemIds, action.boardId, undefined, false);
        say('Redo: Folder created');
      }
      reload = true;
      break;
    }

    case 'CREATE_ARROWS': {
      if (isUndo) {
        if (!action.arrowIds?.length) break;
        for (const id of action.arrowIds) await useArrowStore.getState().deleteArrow(id);
        say('Undo: Connections removed');
      } else {
        const arrows = (action.newContent as { arrows?: Array<Partial<Arrow>> } | undefined)?.arrows;
        if (!arrows?.length) break;
        // New ids: history keeps the arrows' data, not the rows
        const created = await useArrowStore.getState().createArrowsBatch(arrows.map(({ id: _id, ...rest }) => rest));
        action.arrowIds = created.map((a) => a.id);
        say('Redo: Connections created');
      }
      break;
    }

    case 'CREATE_ITEM':
    case 'DELETE_ITEM':
    case 'DELETE_ITEMS': {
      const items = action.items ?? [];
      if (items.length === 0) break;
      // Undoing a delete and redoing a create both bring the items back
      const bringBack = isUndo !== (action.type === 'CREATE_ITEM');
      if (bringBack) {
        await store.restoreItems(items);
        say(`${label}: ${items.length} item${items.length === 1 ? '' : 's'} restored`);
      } else {
        await store.deleteItems(items.map((i) => i.id));
        say(`${label}: ${items.length} item${items.length === 1 ? '' : 's'} removed`);
      }
      break;
    }

    case 'MOVE_ITEM':
    case 'MOVE_ITEMS': {
      const positions = isUndo ? action.previousPositions : action.newPositions;
      if (positions) {
        await store.applyItemPositions(positions);
        say(isUndo ? 'Undo: Position restored' : 'Redo: Position applied');
      }
      break;
    }

    case 'UPDATE_CONTENT': {
      const content = isUndo ? action.previousContent : action.newContent;
      if (action.itemId && content) {
        setItemContent(action.itemId, content);
        say(isUndo ? 'Undo: Content restored' : 'Redo: Content applied');
      }
      break;
    }

    case 'GROUP_CLUSTER': {
      // Cards back where they were, and the board's clusters as they were
      const positions = isUndo ? action.previousPositions : action.newPositions;
      const clusters = isUndo ? action.previousClusters : action.newClusters;
      const clustering = isUndo ? action.previousClustering : action.newClustering;
      if (positions) await store.applyItemPositions(positions);
      if (clusters && clustering) await store.restoreClusters(clusters, clustering);
      say(isUndo ? 'Undo: Cluster removed' : 'Redo: Cluster grouped');
      break;
    }

    case 'UNGROUP_CLUSTER': {
      const clusters = isUndo ? action.previousClusters : action.newClusters;
      const clustering = isUndo ? action.previousClustering : action.newClustering;
      if (clusters && clustering) await store.restoreClusters(clusters, clustering);
      say(isUndo ? 'Undo: Cluster restored' : 'Redo: Cluster ungrouped');
      break;
    }

    case 'MOVE_TO_BOARD': {
      const boardId = isUndo ? action.previousBoardId : action.newBoardId;
      if (action.itemIds && boardId) {
        await moveToBoard(action.itemIds, boardId, isUndo ? action.previousPositions : action.newPositions, false);
        reload = true;
        say(isUndo ? 'Undo: Items moved back' : 'Redo: Items moved');
      }
      break;
    }
  }
  // A step on its own reloads now; inside a group, the group reloads once at the end
  if (reload && !quiet) {
    await reloadBoard();
    return false;
  }
  return reload;
}

export function useUndoRedo() {
  const canUndo = useHistoryStore((s) => s.undoStack.length > 0);
  const canRedo = useHistoryStore((s) => s.redoStack.length > 0);

  const executeUndo = useCallback(() => enqueueStep('undo'), []);
  const executeRedo = useCallback(() => enqueueStep('redo'), []);

  useEffect(() => {
    runUndo = executeUndo;
    runGroupUndo = runGroupStep;
    return () => {
      if (runUndo === executeUndo) runUndo = null;
      if (runGroupUndo === runGroupStep) runGroupUndo = null;
    };
  }, [executeUndo]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      // Shift turns the key into 'Z' on some platforms
      const key = e.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;

      // Typing, or inside a dialog/menu: Cmd+Z there must not undo canvas changes behind it
      if (isFocusInTextOrOverlay(document.activeElement)) return;

      e.preventDefault();
      // A held key auto-repeats; one press is one step
      if (e.repeat) return;

      if (key === 'z' && !e.shiftKey) void executeUndo();
      else void executeRedo();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [executeUndo, executeRedo]);

  return {
    executeUndo,
    executeRedo,
    canUndo,
    canRedo,
  };
}
